import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import AnimateEdit from "./AnimateEdit";
import { useAnimate } from "../../backend/useAnimate";
import { useUser } from "../../backend/useUser";
import AnimateInitial from "./AnimateInitial";
import FreeTrialTrigger from "../pricing/FreeTrialTrigger";
import { useConfig } from "../../utils/config";

export const DEFAULT_ANIMATION_NAME = 'New Animation';

const Animate = () => {
    const config = useConfig();
    const navigate = useNavigate();
    const location = useLocation();
    const { animationId: urlAnimationId } = useParams();
    const [animationId, setAnimationId] = useState(urlAnimationId);
    const [isLoading, setIsLoading] = useState(!!urlAnimationId);
    const { createAnimation, getAnimation } = useAnimate();
    const { user, loading: userLoading } = useUser();

    // Set view based on URL pattern
    useEffect(() => {
        if (urlAnimationId) {
            setAnimationId(urlAnimationId);
            // console.log('urlAnimationId:', urlAnimationId);
        }
    }, [urlAnimationId]);
    
    // Load animation data from database when animationId changes
    useEffect(() => {
        if (!animationId || userLoading) return;

        const fetchAnimationData = async () => {
            setIsLoading(true);
            try {
                const animationData = await getAnimation(animationId);
                if (animationData) {
                    const isOwner = user?.id === animationData.user_id;
                    const isAdmin = config.ADMIN_EMAILS?.includes(user?.email || '');

                    if (isOwner || isAdmin) {
                        // Name is now handled inside AnimateEdit
                    } else {
                        setAnimationId(undefined);
                        navigate('/animate');
                    }
                }
            } catch (error) {
                console.error('Error fetching animation data:', error);
                setAnimationId(undefined);
                navigate('/animate');
            } finally {
                setIsLoading(false);
            }
        };

        fetchAnimationData();
    }, [animationId, getAnimation, user, userLoading, config.ADMIN_EMAILS, navigate]);

    const handleNotLoggedIn = () => {
        if (!user) {
            // console.log('User not authenticated, storing redirect path:', location.pathname);
            localStorage.setItem('redirectPath', location.pathname + location.search);
            navigate('/signup', { state: { from: location } });
            return true;
        }
        return false;
    }

    const handleCreateClick = async () => {
        if (handleNotLoggedIn()) return;

        // console.log('Creating new animation');
        try {
            setIsLoading(true);
            // Create a new animation entry in the database
            const newAnimation = await createAnimation(DEFAULT_ANIMATION_NAME);
            // console.log('NEW ANIMATION:', newAnimation);
            
            // Navigate to the new animation page
            setAnimationId(newAnimation.id);
            navigate(`/animate/${newAnimation.id}`);
        } catch (error) {
            console.error('Error creating animation:', error);
            setIsLoading(false);
        }
    };

    if (animationId) {
        return (
            <>
                <AnimateEdit
                    config={config}
                    animationId={animationId}
                    isLoading={isLoading}
                />
            </>
        );
    } else {
        return (
            <>
                <FreeTrialTrigger colorMode="light" />
                <AnimateInitial 
                    config={config}
                    onCreateClick={handleCreateClick} 
                />
            </>
        );
    }  
};

export default Animate;